// Bonus redemption: max % from admin settings - UPDATED_2025_10_01
import { useState, useEffect } from 'react';
import { createDeliveryOrder, getLastDeliveryAddress, calculateDelivery, CartItem } from '../../api/restaurant/ordersApi';
import { useAuthStore } from '@shared/store/authStore';
import { saveRestaurantOrder } from '../../utils/restaurantOrders';

interface OrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  cartItems: CartItem[];
  user?: any;
  onOrderComplete?: () => void;
}

const OrderModal: React.FC<OrderModalProps> = ({ isOpen, onClose, cartItems, user: userProp, onOrderComplete }) => {
  const { user: authUser, updateUser, apiClient } = useAuthStore();

  // Используем authUser из store как основной источник данных
  const rawUser = authUser || userProp;

  // КРИТИЧЕСКИ ВАЖНО: Нормализуем телефон сразу
  const user = rawUser ? {
    ...rawUser,
    phone: typeof rawUser.phone === 'object' && rawUser.phone?.Phone
      ? rawUser.phone.Phone
      : rawUser.phone
  } : null;


  const [step, setStep] = useState('delivery'); // delivery, confirm, success
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [orderResult, setOrderResult] = useState(null);
  const [deliveryInfo, setDeliveryInfo] = useState(null); // информация о доставке

  // Состояния для бонусов
  const [bonusToUse, setBonusToUse] = useState(0);
  const [userBonusBalance, setUserBonusBalance] = useState(0);
  const [bonusBalanceLoading, setBonusBalanceLoading] = useState(false);
  const [maxRedemptionPercent, setMaxRedemptionPercent] = useState(40);

  // Состояние для полной информации пользователя из Universe API
  const [fullUserInfo, setFullUserInfo] = useState(null);

  // Данные для заказа
  const [orderData, setOrderData] = useState({
    deliveryType: 'beach_club', // delivery, pickup или beach_club
    address: '', // Объединенное поле улица + дом
    apartmentNumber: '',
    entrance: '',
    floor: '',
    comment: '',
    deliveryTime: 'asap', // Время доставки
    paymentType: 'cash', // cash или card
    firstName: '', // Имя клиента
    utensilsCount: 0, // Количество приборов
    lat: null, // Координаты адреса
    lon: null
  });

  // Загрузка настроек бонусов из админ-панели
  useEffect(() => {
    if (!isOpen) return;
    fetch('/api/settings/bonuses')
      .then(res => res.json())
      .then(data => {
        if (data.success && data.settings?.maxRedemptionPercent) {
          setMaxRedemptionPercent(data.settings.maxRedemptionPercent);
        }
      })
      .catch(() => {}); // fallback to default 40%
  }, [isOpen]);

  // Загрузка баланса бонусов при открытии модала - КАК В PROFILEPAGE
  useEffect(() => {
    console.log('🔍 [OrderModal] useEffect бонусов:', { isOpen, phone: user?.phone, hasApiClient: !!apiClient });

    if (!isOpen || !user?.phone || !apiClient) {
      console.log('❌ [OrderModal] Условия не выполнены, выход');
      return;
    }

    console.log('✅ [OrderModal] Загружаем бонусы...');
    setBonusBalanceLoading(true);
    const loadBonusBalance = async () => {
      try {
        console.log('📞 [OrderModal] Вызываем checkPhoneExists...');
        const clientResult = await apiClient.checkPhoneExists(user.phone);
        console.log('📋 [OrderModal] checkPhoneExists result:', clientResult);

        if (clientResult.exists && clientResult.clientInfo?.ClientId) {
          const clientId = clientResult.clientInfo.ClientId;
          // Загружаем effective balance с учётом expiry
          try {
            console.log('📞 [OrderModal] Загружаем effective-balance...');
            const resp = await fetch(`/api/bonuses/${clientId}/effective-balance`);
            const data = await resp.json();
            if (data.success && data.balance) {
              const bonusBalance = data.balance.effective ?? 0;
              console.log('💰 [OrderModal] Effective bonusBalance:', bonusBalance);
              setUserBonusBalance(bonusBalance);
              return;
            }
          } catch (e) {
            console.warn('⚠️ [OrderModal] effective-balance fallback:', e);
          }
          // Fallback на raw balance
          const fullInfo = await apiClient.getClient(clientId);
          if (fullInfo) {
            const bonusBalance = fullInfo.bonus_balance || 0;
            console.log('💰 [OrderModal] Fallback bonusBalance:', bonusBalance);
            setUserBonusBalance(bonusBalance);
          }
        }
      } catch (error) {
        console.error('❌ [OrderModal] Ошибка:', error);
        setUserBonusBalance(0);
      } finally {
        setBonusBalanceLoading(false);
      }
    };
    loadBonusBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user?.phone]);

  // Сброс состояния при закрытии модала
  useEffect(() => {
    if (!isOpen) {
      setStep('delivery');
      setLoading(false);
      setError('');
      setOrderResult(null);
      setDeliveryInfo(null);
      setBonusToUse(0);
      // Сбрасываем userBonusBalance чтобы при следующем открытии загрузить свежие данные
      setUserBonusBalance(0);
      setBonusBalanceLoading(false);
      setFullUserInfo(null);
      setOrderData({
        deliveryType: 'beach_club',
        address: '',
        apartmentNumber: '',
        entrance: '',
        floor: '',
        comment: '',
        deliveryTime: 'asap',
        paymentType: 'cash',
        firstName: '',
        utensilsCount: 0,
        lat: null,
        lon: null
      });
    }
  }, [isOpen]);

  // Загрузка последнего адреса и профиля лояльности при открытии модала
  useEffect(() => {
    if (isOpen && user?.phone) {
      getLastDeliveryAddress(user.phone).then(lastAddress => {
        if (lastAddress && lastAddress.lastDeliveryAddress) {
          setOrderData(prev => ({
            ...prev,
            address: lastAddress.lastDeliveryAddress,
            lat: lastAddress.lat,
            lon: lastAddress.lon
          }));
        }
      }).catch(error => {
        // Ошибка при загрузке последнего адреса
      });

    }
  }, [isOpen, user?.phone, apiClient]); // Добавляем apiClient в зависимости

  // Дополнительный useEffect для обновления баланса после списания бонусов
  useEffect(() => {
    // Если пользователь вернулся в модал после успешного заказа, обновляем баланс
    if (isOpen && step === 'delivery' && user?.phone && !bonusBalanceLoading) {

      const refreshBalance = async () => {
        try {
          const store = useAuthStore.getState();
          if (store.getBonusBalance) {
            const balance = await store.getBonusBalance(user.phone);
            setUserBonusBalance(balance);
          }
        } catch (error) {
          // Ошибка при обновлении баланса
        }
      };

      refreshBalance();
    }
  }, [step, isOpen, user?.phone, bonusBalanceLoading]);

  // Максимальная сумма бонусов рассчитывается динамически (40% от суммы заказа)

  // Стили компонента
  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    modal: {
      backgroundColor: 'var(--modal-bg)',
      borderRadius: '12px',
      width: '100%',
      maxWidth: '500px',
      maxHeight: '90vh',
      overflow: 'auto',
      boxShadow: 'var(--shadow-lg)'
    },
    header: {
      padding: '24px 24px 0',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: '1px solid var(--border-color)',
      paddingBottom: '16px',
      marginBottom: '24px'
    },
    title: {
      fontSize: '20px',
      fontWeight: '600',
      color: 'var(--text-primary)'
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: '24px',
      cursor: 'pointer',
      color: 'var(--text-secondary)',
      padding: '4px'
    },
    content: {
      padding: '0 24px 24px'
    },
    formGroup: {
      marginBottom: '16px'
    },
    label: {
      display: 'block',
      marginBottom: '6px',
      fontSize: '14px',
      fontWeight: '500',
      color: 'var(--text-primary)'
    },
    input: {
      width: '100%',
      padding: '12px',
      border: '1px solid var(--border-color)',
      borderRadius: '6px',
      fontSize: '16px',
      outline: 'none',
      transition: 'border-color 0.2s',
      boxSizing: 'border-box',
      backgroundColor: 'var(--input-bg)',
      color: 'var(--text-primary)'
    },
    select: {
      width: '100%',
      padding: '12px',
      border: '1px solid var(--border-color)',
      borderRadius: '6px',
      fontSize: '16px',
      outline: 'none',
      transition: 'border-color 0.2s',
      boxSizing: 'border-box',
      backgroundColor: 'var(--input-bg)',
      color: 'var(--text-primary)',
      cursor: 'pointer'
    },
    textarea: {
      width: '100%',
      padding: '12px',
      border: '1px solid var(--border-color)',
      borderRadius: '6px',
      fontSize: '16px',
      outline: 'none',
      transition: 'border-color 0.2s',
      boxSizing: 'border-box',
      minHeight: '80px',
      resize: 'vertical',
      backgroundColor: 'var(--input-bg)',
      color: 'var(--text-primary)'
    },
    row: {
      display: 'flex',
      gap: '12px'
    },
    halfWidth: {
      flex: 1
    },
    button: {
      width: '100%',
      padding: '12px 24px',
      backgroundColor: '#10b981',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      fontSize: '16px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'background-color 0.2s'
    },
    buttonSecondary: {
      width: '100%',
      padding: '12px 24px',
      backgroundColor: 'var(--button-bg)',
      color: 'var(--text-primary)',
      border: '1px solid var(--border-color)',
      borderRadius: '6px',
      fontSize: '16px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      marginTop: '12px'
    },
    error: {
      color: '#ef4444',
      fontSize: '14px',
      marginTop: '8px',
      padding: '12px',
      backgroundColor: '#fef2f2',
      border: '1px solid #fecaca',
      borderRadius: '6px'
    },
    orderSummary: {
      backgroundColor: 'var(--card-bg)',
      padding: '16px',
      borderRadius: '8px',
      marginBottom: '24px'
    },
    orderItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '8px 0',
      borderBottom: '1px solid var(--border-color)',
      color: 'var(--text-primary)'
    },
    orderItemLast: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '8px 0',
      fontWeight: '600',
      fontSize: '16px',
      color: 'var(--text-primary)'
    },
    successIcon: {
      fontSize: '48px',
      color: '#10b981',
      textAlign: 'center',
      marginBottom: '16px'
    },
    successMessage: {
      textAlign: 'center',
      fontSize: '18px',
      fontWeight: '500',
      marginBottom: '24px'
    },

    // Стили для бонусной системы
  };

  // Функция расчета доставки
  const calculateDeliveryInfo = async (address: string) => {
    if (!address || orderData.deliveryType !== 'delivery') {
      setDeliveryInfo(null);
      return;
    }

    try {
      const result = await calculateDelivery(address);
      setDeliveryInfo(result);
    } catch (error) {
      // Ошибка расчета доставки
      setDeliveryInfo(null);
      // Не показываем ошибку пользователю для расчета доставки
    }
  };

  // Debounce функция для расчета доставки
  useEffect(() => {
    if (orderData.address && orderData.deliveryType === 'delivery') {
      const timeoutId = setTimeout(() => {
        calculateDeliveryInfo(orderData.address);
      }, 1000); // задержка 1 секунда после ввода

      return () => clearTimeout(timeoutId);
    } else {
      setDeliveryInfo(null);
    }
  }, [orderData.address, orderData.deliveryType]);

  // Обработчики ввода
  const handleInputChange = async (field: string, value: any) => {
    setOrderData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };

      // При смене типа доставки
      if (field === 'deliveryType') {
        if (value === 'pickup') {
          // Для самовывоза ставим адрес ресторана
          newData.address = 'Республика Крым, Симферополь, Гурзуфская улица, 9';
          newData.apartmentNumber = '';
          newData.entrance = '';
          newData.floor = '';
        } else if (value === 'beach_club' || value === 'panorama') {
          // Для ресторанов очищаем адресные поля
          newData.address = '';
          newData.apartmentNumber = '';
          newData.entrance = '';
          newData.floor = '';
        }
      }

      return newData;
    });
  };

  // Вычисление общей суммы
  const calculateTotal = () => {
    const itemsTotal = calculateItemsTotal();

    // Добавляем стоимость доставки
    const deliveryCost = deliveryInfo?.deliveryCost || 0;
    return itemsTotal + deliveryCost;
  };

  // Вычисление суммы только за товары (с учётом модификаторов)
  const calculateItemsTotal = () => {
    return cartItems.reduce((total, item: any) => {
      const price = item.price || 0;
      const quantity = item.quantity || 1;
      const modPrice = (item.modifiers || []).reduce((s: number, m: any) => s + (m.price || 0) * (m.count || 1), 0);
      return total + (price + modPrice) * quantity;
    }, 0);
  };

  // Функции для расчета итоговой суммы

  const calculateFinalTotal = () => {
    const itemsTotal = calculateItemsTotal();
    const deliveryCost = deliveryInfo?.deliveryCost || 0;
    const bonusAmount = bonusToUse || 0;

    return itemsTotal + deliveryCost - bonusAmount;
  };

  // Обработка отправки заказа
  const handleSubmitOrder = async () => {
    if (step !== 'confirm') return;

    setLoading(true);
    setError('');

    try {
      // Формируем адрес доставки
      const fullAddress = `${orderData.address}${orderData.apartmentNumber ? ', кв. ' + orderData.apartmentNumber : ''}${orderData.entrance ? ', подъезд ' + orderData.entrance : ''}${orderData.floor ? ', этаж ' + orderData.floor : ''}`;

      const address = {
        fullAddress,
        address: orderData.address,
        apartmentNumber: orderData.apartmentNumber,
        entrance: orderData.entrance,
        floor: orderData.floor,
        comment: orderData.comment,
        lat: orderData.lat,
        lon: orderData.lon
      };

      // Используем телефон из данных пользователя
      const userPhone = user?.phone || '';


      // Формируем комментарий с информацией о заказе
      let comment = '';

      // Добавляем тип получения заказа (для кухни - в какой посуде подавать)
      const deliveryTypeText = orderData.deliveryType === 'beach_club' ? 'Level' : orderData.deliveryType === 'panorama' ? 'Panorama' : 'Самовывоз';
      comment = deliveryTypeText;

      // Добавляем количество приборов для доставки
      const utensilsCount = orderData.utensilsCount || 0;
      if (utensilsCount > 0) {
        comment += `\nКоличество приборов: ${utensilsCount}`;
      }

      // Добавляем пользовательский комментарий
      if (orderData.comment) {
        comment += `\n${orderData.comment}`;
      }

      const orderRequestData = {
        cartItems,
        address,
        phone: userPhone,
        expectedAt: orderData.deliveryTime === 'asap' ? '' : orderData.deliveryTime,
        expeditionType: orderData.deliveryType,
        paymentType: orderData.paymentType,
        comment: comment,
        // Параметры бонусов
        useBonusPayment: bonusToUse > 0,
        bonusAmount: bonusToUse || 0,
        // Передаем актуальные данные пользователя из Universe API
        userInfo: {
          id: fullUserInfo?.ClientId || authUser?.ClientId,
          firstName: fullUserInfo?.first_name || authUser?.first_name,
          lastName: fullUserInfo?.last_name || authUser?.last_name,
          phone: authUser?.phone
        }
      };

      const result = await createDeliveryOrder(orderRequestData);

      setOrderResult(result);
      setStep('success');

      // Сохраняем заказ в локальную историю
      const orderId = result?.orderId || result?.result?.orderId || '';
      if (orderId) {
        saveRestaurantOrder({
          orderId,
          date: new Date().toISOString(),
          items: cartItems.map(item => ({
            id: item.id || '',
            name: item.name,
            price: item.price,
            quantity: item.quantity
          })),
          total: calculateFinalTotal(),
          deliveryType: orderData.deliveryType,
          paymentType: orderData.paymentType,
          bonusUsed: bonusToUse || 0,
          status: 'pending'
        });
      }

      // Очищаем корзину после успешного заказа
      if (onOrderComplete) {
        onOrderComplete(orderId);
      }

    } catch (error) {
      // Ошибка при создании заказа
      setError(error.message || 'Произошла ошибка при оформлении заказа');
    } finally {
      setLoading(false);
    }
  };

  // Генерация временных слотов (каждые 30 минут, начиная с +30 мин)
  const generateTimeSlots = () => {
    const slots = [];

    // Получаем текущее московское время
    const moscowNow = new Date().toLocaleString("en-US", {timeZone: "Europe/Moscow"});
    const now = new Date(moscowNow);

    // Начинаем с текущего московского времени + 30 минут
    const startTime = new Date(now.getTime() + 30 * 60 * 1000);

    // Округляем до ближайшего получаса
    const minutes = startTime.getMinutes();
    if (minutes < 30) {
      startTime.setMinutes(30, 0, 0);
    } else {
      startTime.setMinutes(0, 0, 0);
      startTime.setHours(startTime.getHours() + 1);
    }

    // Генерируем 20 слотов (10 часов) с интервалом 30 минут
    for (let i = 0; i < 20; i++) {
      const slotTime = new Date(startTime.getTime() + i * 30 * 60 * 1000);

      // Форматируем время
      const timeString = slotTime.toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit'
      });
      const isoString = slotTime.toISOString().slice(0, 19).replace('T', ' ');

      slots.push({
        display: timeString,
        value: isoString
      });
    }

    return slots;
  };

  // Инициализация времени доставки и загрузка последнего адреса при открытии модального окна
  useEffect(() => {
    if (isOpen) {
      // Время по умолчанию — "В ближайшее время"
      if (!orderData.deliveryTime) {
        setOrderData(prev => ({ ...prev, deliveryTime: 'asap' }));
      }

      // Загрузка последнего адреса доставки
      const loadLastDeliveryAddress = async () => {
        if (user?.phone && (orderData.deliveryType === 'delivery' || !orderData.deliveryType) && !orderData.address) {
          try {
            const lastAddress = await getLastDeliveryAddress(user.phone);

            if (lastAddress) {
              setOrderData(prev => ({
                ...prev,
                address: lastAddress.lastDeliveryAddress || lastAddress,
                lat: lastAddress.lat,
                lon: lastAddress.lon
              }));
            } else {
            }
          } catch (error) {
            // Ошибка при загрузке последнего адреса
            // Не показываем ошибку пользователю, просто продолжаем без адреса
          }
        }
      };

      loadLastDeliveryAddress();
    }
  }, [isOpen, user?.phone]);

  // Переход к подтверждению
  const handleNextStep = () => {
    // Для типа "pickup" нужен адрес ресторана (уже заполнен автоматически)
    if (orderData.deliveryType === 'pickup') {
      if (!orderData.address) {
        setError('Адрес ресторана не указан');
        return;
      }
    }
    // Для типа "beach_club" (Level) и "panorama" (Panorama) валидация адреса не нужна

    // Валидация бонусов: максимум N% от суммы заказа (из настроек)
    if (bonusToUse > 0) {
      const orderTotal = calculateItemsTotal();
      const maxBonusAllowed = Math.floor(orderTotal * (maxRedemptionPercent / 100));

      console.log('🔍 Bonus validation:', {
        bonusToUse,
        orderTotal,
        maxBonusAllowed,
        maxRedemptionPercent,
        userBonusBalance,
        exceeds: bonusToUse > maxBonusAllowed
      });

      if (bonusToUse > maxBonusAllowed) {
        const errorMsg = `Превышен лимит списания бонусов. Максимум: ${maxBonusAllowed}₽ (${maxRedemptionPercent}% от суммы заказа)`;
        console.error('❌ Bonus limit exceeded:', errorMsg);
        setError(errorMsg);
        return;
      }

      if (bonusToUse > userBonusBalance) {
        const errorMsg = `Недостаточно бонусов. Доступно: ${userBonusBalance}₽`;
        console.error('❌ Insufficient bonus balance:', errorMsg);
        setError(errorMsg);
        return;
      }

      console.log('✅ Bonus validation passed');
    }

    setError('');
    setStep('confirm');
  };

  // Если пользователь не авторизован - показываем сообщение
  if (!user) {
    if (!isOpen) return null;

    return (
      <div style={styles.overlay} onClick={onClose}>
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div style={styles.header}>
            <h2 style={styles.title}>Требуется авторизация</h2>
            <button style={styles.closeButton} onClick={onClose}>×</button>
          </div>
          <div style={styles.content}>
            <div style={{
              textAlign: 'center',
              padding: '40px 20px'
            }}>
              <div style={{
                fontSize: '48px',
                marginBottom: '16px'
              }}>🔒</div>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                marginBottom: '12px',
                color: 'var(--text-primary)'
              }}>
                Войдите в аккаунт
              </h3>
              <p style={{
                color: 'var(--text-secondary)',
                marginBottom: '24px',
                lineHeight: '1.5'
              }}>
                Для оформления заказа необходимо войти в аккаунт через главное меню приложения
              </p>
              <button
                style={styles.button}
                onClick={onClose}
              >
                Понятно
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>
            {step === 'delivery' && 'Оформить заказ'}
            {step === 'confirm' && 'Подтвердите заказ'}
            {step === 'success' && 'Заказ оформлен'}
          </h2>
          <button style={styles.closeButton} onClick={onClose}>×</button>
        </div>

        <div style={styles.content}>
          {step === 'delivery' && (
            <>
              <div style={styles.formGroup}>
                <label style={styles.label}>Тип получения заказа *</label>
                <select
                  style={styles.select}
                  value={orderData.deliveryType}
                  onChange={(e) => handleInputChange('deliveryType', e.target.value)}
                >
                  <option value="beach_club">Level</option>
                  <option value="panorama">Panorama</option>
                  <option value="pickup">Самовывоз</option>
                </select>
              </div>

              {/* Поле адреса - скрываем для пляжного клуба */}
              {orderData.deliveryType !== 'beach_club' && orderData.deliveryType !== 'panorama' && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    {orderData.deliveryType === 'delivery' ? 'Адрес доставки' : 'Адрес ресторана'} *
                  </label>
                  <input
                    style={{
                      ...styles.input,
                      backgroundColor: orderData.deliveryType === 'pickup' ? 'var(--input-bg)' : 'var(--input-bg)',
                      color: 'var(--text-primary)'
                    }}
                    type="text"
                    placeholder={
                      orderData.deliveryType === 'delivery'
                        ? 'Начните вводить адрес (улица, дом)...'
                        : 'Адрес ресторана'
                    }
                    value={orderData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    disabled={orderData.deliveryType === 'pickup'}
                  />
                </div>
              )}


              {orderData.deliveryType === 'delivery' && (
                <div style={styles.row}>
                  <div style={styles.halfWidth}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Квартира</label>
                      <input
                        style={styles.input}
                        type="text"
                        placeholder="№ кв."
                        value={orderData.apartmentNumber}
                        onChange={(e) => handleInputChange('apartmentNumber', e.target.value)}
                      />
                    </div>
                  </div>
                  <div style={styles.halfWidth}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Подъезд</label>
                      <input
                        style={styles.input}
                        type="text"
                        placeholder="№ подъезда"
                        value={orderData.entrance}
                        onChange={(e) => handleInputChange('entrance', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}

              {orderData.deliveryType === 'delivery' && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Этаж</label>
                  <input
                    style={styles.input}
                    type="text"
                    placeholder="№ этажа"
                    value={orderData.floor}
                    onChange={(e) => handleInputChange('floor', e.target.value)}
                  />
                </div>
              )}

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  {(orderData.deliveryType === 'beach_club' || orderData.deliveryType === 'panorama') ? 'Время подачи' : orderData.deliveryType === 'pickup' ? 'Время выдачи' : 'Время доставки'} *
                </label>
                <select
                  style={styles.select}
                  value={orderData.deliveryTime}
                  onChange={(e) => handleInputChange('deliveryTime', e.target.value)}
                >
                  <option value="asap">В ближайшее время</option>
                  {generateTimeSlots().map((slot, index) => (
                    <option key={index} value={slot.value}>
                      {slot.display}
                    </option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Способ оплаты *</label>
                <select
                  style={styles.select}
                  value={orderData.paymentType}
                  onChange={(e) => handleInputChange('paymentType', e.target.value)}
                >
                  <option value="cash">💵 Наличными</option>
                  <option value="card">💳 Картой</option>
                </select>
              </div>

              {/* Количество приборов - только для доставки */}
              {orderData.deliveryType === 'delivery' && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Количество приборов</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <button
                      type="button"
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '4px',
                        border: '1px solid var(--border-color)',
                        backgroundColor: 'var(--input-bg)',
                        color: 'var(--text-primary)',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s'
                      }}
                      onClick={() => setOrderData(prev => ({
                        ...prev,
                        utensilsCount: Math.max(0, (prev.utensilsCount || 0) - 1)
                      }))}
                    >
                      −
                    </button>
                    <span style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      minWidth: '30px',
                      textAlign: 'center',
                      color: 'var(--text-primary)'
                    }}>
                      {orderData.utensilsCount || 0}
                    </span>
                    <button
                      type="button"
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '4px',
                        border: '1px solid var(--border-color)',
                        backgroundColor: 'var(--input-bg)',
                        color: 'var(--text-primary)',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s'
                      }}
                      onClick={() => setOrderData(prev => ({
                        ...prev,
                        utensilsCount: (prev.utensilsCount || 0) + 1
                      }))}
                    >
                      +
                    </button>
                  </div>
                </div>
              )}

              <div style={styles.formGroup}>
                <label style={styles.label}>Комментарий к заказу</label>
                <textarea
                  style={styles.textarea}
                  placeholder="Дополнительная информация (домофон, ориентиры и т.д.)"
                  value={orderData.comment}
                  onChange={(e) => handleInputChange('comment', e.target.value)}
                />
              </div>

              {/* Блок оплаты бонусами */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Оплата бонусами</label>

                {bonusBalanceLoading ? (
                  <div style={{
                    padding: '12px',
                    textAlign: 'center',
                    color: '#6b7280',
                    backgroundColor: '#f9fafb',
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb'
                  }}>
                    ⏳ Загружаем баланс бонусов...
                  </div>
                ) : userBonusBalance === 0 ? (
                  <div style={{
                    padding: '12px',
                    textAlign: 'center',
                    color: '#6b7280',
                    backgroundColor: '#fef3f2',
                    borderRadius: '6px',
                    border: '1px solid #fecaca'
                  }}>
                    💸 На вашем бонусном счете нет средств
                    <div style={{ fontSize: '12px', marginTop: '4px' }}>
                      Бонусы начисляются за покупки и участие в программе лояльности
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{
                      padding: '8px 12px',
                      backgroundColor: '#f0fdf4',
                      borderRadius: '6px',
                      border: '1px solid #bbf7d0',
                      marginBottom: '8px',
                      fontSize: '14px',
                      color: '#15803d'
                    }}>
                      💰 Доступно бонусов: <strong>{userBonusBalance}₽</strong>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <input
                        type="number"
                        placeholder="Сумма бонусов"
                        min="0"
                        max={Math.min(userBonusBalance, Math.floor(calculateItemsTotal() * (maxRedemptionPercent / 100)))}
                        value={bonusToUse}
                        onInput={(e: any) => {
                          // Жестко ограничиваем ввод на уровне HTML
                          const inputValue = parseInt(e.target.value) || 0;
                          const maxByBalance = userBonusBalance;
                          const maxByPercent = Math.floor(calculateItemsTotal() * (maxRedemptionPercent / 100));
                          const maxValue = Math.min(maxByBalance, maxByPercent);
                          if (inputValue > maxValue) {
                            e.target.value = maxValue;
                            setBonusToUse(maxValue);
                          } else {
                            setBonusToUse(inputValue);
                          }
                        }}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 0;
                          const maxByBalance = userBonusBalance;
                          const maxByPercent = Math.floor(calculateItemsTotal() * (maxRedemptionPercent / 100));
                          const maxValue = Math.min(maxByBalance, maxByPercent);
                          setBonusToUse(Math.min(value, maxValue));
                        }}
                        style={{
                          ...styles.input,
                          flex: 1
                        }}
                      />
                      <span style={{
                        fontSize: '14px',
                        color: '#6b7280',
                        whiteSpace: 'nowrap'
                      }}>
                        (макс. {Math.min(userBonusBalance, Math.floor(calculateItemsTotal() * (maxRedemptionPercent / 100)))}₽)
                      </span>
                    </div>
                  </>
                )}
              </div>

              {error && <div style={styles.error}>{error}</div>}

              <button style={styles.button} onClick={handleNextStep}>
                Далее
              </button>
            </>
          )}

          {step === 'confirm' && (
            <>
              <div style={styles.orderSummary}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600' }}>Ваш заказ:</h3>
                {cartItems.map((item: any, index: number) => {
                  const itemMods = item.modifiers || [];
                  const modPrice = itemMods.reduce((s: number, m: any) => s + (m.price || 0) * (m.count || 1), 0);
                  const lineTotal = (item.price + modPrice) * item.quantity;
                  return (
                    <div key={index} style={styles.orderItem}>
                      <div>
                        <div>{item.name} x{item.quantity}</div>
                        {itemMods.length > 0 && (
                          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                            {itemMods.map((m: any) => `+${m.name}${m.count > 1 ? ` ×${m.count}` : ''}`).join(', ')}
                          </div>
                        )}
                      </div>
                      <span>{lineTotal.toFixed(2)} ₽</span>
                    </div>
                  );
                })}
                {/* Показываем стоимость доставки отдельно */}
                {orderData.deliveryType === 'delivery' && deliveryInfo && deliveryInfo.deliveryCost > 0 && (
                  <div style={styles.orderItem}>
                    <span>Доставка:</span>
                    <span>{deliveryInfo.deliveryCost} ₽</span>
                  </div>
                )}


                {/* Использованные бонусы - ТОЛЬКО ЕСЛИ КЛИЕНТ ВЫБРАЛ */}
                {bonusToUse > 0 && (
                  <div style={styles.orderItem}>
                    <span>Использовано бонусов:</span>
                    <span style={{ color: '#10b981' }}>-{bonusToUse} ₽</span>
                  </div>
                )}

                <div style={styles.orderItemLast}>
                  <span>Итого к доплате:</span>
                  <span>{calculateFinalTotal().toFixed(2)} ₽</span>
                </div>


              </div>

              <div style={styles.orderSummary}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600' }}>
                  {(orderData.deliveryType === 'beach_club' || orderData.deliveryType === 'panorama') ? 'Информация о получении:' : 'Адрес самовывоза:'}
                </h3>

                {(orderData.deliveryType === 'beach_club' || orderData.deliveryType === 'panorama') ? (
                  <p style={{ margin: '0 0 8px 0' }}>
                    <strong>Способ получения:</strong> {orderData.deliveryType === 'beach_club' ? 'Level' : 'Panorama'}
                  </p>
                ) : (
                  <p style={{ margin: '0 0 8px 0' }}>
                    {orderData.address}
                    {orderData.apartmentNumber && `, кв. ${orderData.apartmentNumber}`}
                    {orderData.entrance && `, подъезд ${orderData.entrance}`}
                    {orderData.floor && `, этаж ${orderData.floor}`}
                  </p>
                )}

                <p style={{ margin: '0 0 8px 0' }}>
                  <strong>Время {(orderData.deliveryType === 'beach_club' || orderData.deliveryType === 'panorama') ? 'подачи' : 'выдачи'}:</strong> {
                    orderData.deliveryTime === 'asap'
                      ? 'В ближайшее время'
                      : (() => {
                          const timeSlots = generateTimeSlots();
                          const selectedSlot = timeSlots.find(slot => slot.value === orderData.deliveryTime);
                          return selectedSlot ? selectedSlot.display : 'Не указано';
                        })()
                  }
                </p>
                <p style={{ margin: '0 0 8px 0' }}>
                  <strong>Способ оплаты:</strong> {
                    orderData.paymentType === 'cash' ? '💵 Наличными' : '💳 Картой'
                  }
                </p>
                {orderData.deliveryType === 'delivery' && deliveryInfo && (
                  <p style={{ margin: '0 0 8px 0' }}>
                    <strong>Ориентировочное время:</strong> {deliveryInfo.deliveryTimeRange} минут
                  </p>
                )}
                {orderData.comment && (
                  <p style={{ margin: '0', fontSize: '14px', color: 'var(--text-secondary)' }}>
                    <strong>Комментарий:</strong> {orderData.comment}
                  </p>
                )}
              </div>

              {error && <div style={styles.error}>{error}</div>}

              <button
                style={styles.button}
                onClick={handleSubmitOrder}
                disabled={loading}
              >
                {loading ? 'Оформляем заказ...' : 'Подтвердить заказ'}
              </button>

              <button
                style={styles.buttonSecondary}
                onClick={() => setStep('delivery')}
                disabled={loading}
              >
                Назад
              </button>
            </>
          )}

          {step === 'success' && (
            <>
              <div style={styles.successIcon}>✅</div>
              <div style={styles.successMessage}>
                Заказ успешно оформлен!
              </div>
              {orderResult && (
                <div style={styles.orderSummary}>
                  <p><strong>Номер заказа:</strong> {orderResult.orderId || orderResult.result?.orderId || 'Неизвестен'}</p>
                  <p>Мы свяжемся с вами в ближайшее время для подтверждения заказа.</p>
                </div>
              )}
              <button style={styles.button} onClick={onClose}>
                Закрыть
              </button>
            </>
          )}
        </div>
      </div>


    </div>
  );
};

export default OrderModal;